import type { FrontRoomsView } from '@obraplan/core/application/contracts/takeoff';
import { ComodosDaFrente } from '@/features/room-progress/ComodosDaFrente';
import { percentual } from '@/features/room-progress/planta-pintada';
import {
  RASCUNHO_VAZIO,
  type RascunhoDosComodos,
} from '@/features/room-progress/rascunho-dos-comodos';
import { useLive } from '@/lib/use-live';

/**
 * O avanço por cômodo no apontamento do dia (Fase 34, etapa 2 — D4, D7, D13, RN-443, RN-444).
 *
 * A frente × serviço que mede por ambiente abre aqui os aptos × cômodos do pavimento, cada um
 * com o percentual do dia e o "não se aplica". É o avanço físico (D8): não preenche a quantidade
 * do dia, que continua à parte — por isso não há soma aqui, como nas peças.
 *
 * Como o `PecasDoDia`, é de leitura e controlada: o pai guarda o rascunho, preso à frente ×
 * serviço, e grava ao salvar o dia. O servidor diz o que valia naquele dia; o rascunho, o que a
 * pessoa mudou. O serviço que não desce a cômodo não desenha nada.
 */
export type CarregarComodos = (input: {
  activityId: string;
  serviceId: string;
  date: string;
}) => Promise<FrontRoomsView | undefined>;

export interface ComodosDoDiaProps {
  activityId: string;
  serviceId: string;
  /** AAAA-MM-DD do dia que se lança. */
  date: string;
  load: CarregarComodos;
  unit?: string | undefined;
  /** O que a pessoa mudou. `undefined` = nada ainda: vale o que o servidor leu. */
  rascunho: RascunhoDosComodos | undefined;
  /** Leva junto a leitura, que é contra ela que o pai calcula o que gravar. */
  onChange: (change: { view: FrontRoomsView; rascunho: RascunhoDosComodos }) => void;
  disabled?: boolean;
}

export function ComodosDoDia({
  activityId,
  serviceId,
  date,
  load,
  unit,
  rascunho,
  onChange,
  disabled = false,
}: ComodosDoDiaProps) {
  const view = useLive(() => load({ activityId, serviceId, date }), [activityId, serviceId, date]);

  // O serviço que não desce a cômodo não desenha nada — nem enquanto carrega.
  if (view === undefined || !view.byRoom) return null;

  if (view.rooms.length === 0) {
    return (
      <p className="text-xs text-(--color-content-muted)">
        Este serviço é por cômodo, mas o pavimento não tem desenho com cômodos: o avanço fica na
        quantidade do dia.
      </p>
    );
  }

  return (
    <fieldset className="flex flex-col gap-2 rounded-md border border-(--color-border-subtle) p-3">
      <legend className="px-1 text-sm font-medium">Avanço por cômodo</legend>
      {view.progressFromRooms !== undefined && (
        <p className="text-xs text-(--color-content-muted)">
          Até {date.split('-').reverse().join('/')}, o item está em{' '}
          {percentual(view.progressFromRooms)} pelos cômodos.
        </p>
      )}
      <ComodosDaFrente
        view={view}
        unit={unit}
        rascunho={rascunho ?? RASCUNHO_VAZIO}
        onChange={(proximo) => onChange({ view, rascunho: proximo })}
        disabled={disabled}
      />
    </fieldset>
  );
}
